import React, { useState, FC } from 'react';
import { Box, Typography, Button, Divider, Paper } from '@mui/material';
import { MaterialGroup, GroupMaterials } from 'models/organization';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import AddMaterialGroupDialog from './add-material-group-dialog.component';
import { useMutation } from '@apollo/client';
import { saveMaterialGroupTemplate, deleteMaterialGroupTemplate } from 'apollo/templates/action-sets';

type MaterialGroupSectionProps = {
    materialGroups: MaterialGroup[];
    actionSetId: string;
    reFetchActionSet?: (actionSetId: string) => void;
};

const MaterialGroupSection: FC<MaterialGroupSectionProps> = ({ materialGroups, actionSetId, reFetchActionSet }) => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const [materialGroup, setMaterialGroup] = useState({} as MaterialGroup);
    const [groupMaterials, setGroupMaterials] = useState([] as GroupMaterials[]);
    const [addMaterialGroupDialogOpen, setAddMaterialGroupDialogOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [saveMaterialGroup] = useMutation(saveMaterialGroupTemplate, {
        onCompleted: () => {
            if (reFetchActionSet) {
                reFetchActionSet(actionSetId);
            }
            closeAddMaterialGroupDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteMaterialGroup] = useMutation(deleteMaterialGroupTemplate, {
        onCompleted: () => {
            if (reFetchActionSet) {
                reFetchActionSet(actionSetId);
            }
            closeAddMaterialGroupDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const onAddEditMaterial = (materialGroup?: MaterialGroup) => {
        setGroupMaterials(materialGroup?.materials || []);
        setMaterialGroup(materialGroup || ({ actionSet: actionSetId } as MaterialGroup));
        setAddMaterialGroupDialogOpen(true);
    };

    const closeAddMaterialGroupDialog = () => {
        setAddMaterialGroupDialogOpen(false);
    };

    const onSaveMaterialGroup = (group: MaterialGroup) => {
        saveMaterialGroup({
            variables: {
                _id: group._id,
                name: group.name,
                materials: group.materials,
                actionSetId,
            },
        });
    };

    const onDeleteMaterialGroup = (groupId: string) => {
        deleteMaterialGroup({
            variables: {
                materialGroupId: groupId,
            },
        });
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('client-directory:MATERIAL_GROUP')}
            </Typography>

            <Divider />

            <Box mt={2} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {materialGroups.map((materialGroup, i) => (
                    <Paper
                        key={i}
                        variant="outlined"
                        onClick={() => onAddEditMaterial(materialGroup)}
                        sx={{
                            p: 1.5,
                            minWidth: 150,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        {i + 1}. {materialGroup.name}
                    </Paper>
                ))}

                <Button
                    onClick={() => onAddEditMaterial()}
                    sx={{
                        height: 'fit-content',
                        minWidth: 150,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    + {translate('common:ADD')}
                </Button>
            </Box>
            <AddMaterialGroupDialog
                open={addMaterialGroupDialogOpen}
                onClose={closeAddMaterialGroupDialog}
                materialGroup={materialGroup}
                groupMaterials={groupMaterials}
                setGroupMaterials={setGroupMaterials}
                setMaterialGroup={setMaterialGroup}
                onSave={onSaveMaterialGroup}
                onDelete={onDeleteMaterialGroup}
            />
        </Box>
    );
};
export default MaterialGroupSection;
